import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import Hls from 'hls.js';
import {
  MediaController,
  MediaControlBar,
  MediaVolumeRange,
  MediaMuteButton,
  MediaFullscreenButton,
  MediaLiveButton,
  MediaTextDisplay,
  MediaLoadingIndicator,
  MediaPlayButton,
} from 'media-chrome/dist/react';

type HlsVideoPlayerProps = {
  src: string;
  broadcastActive: boolean;
  height?: number | string;
  width?: number | string;
  style?: CSSProperties;
};

export function HlsVideoPLayer(props: HlsVideoPlayerProps) {
  const hls = useRef<Hls | null>(null);
  const video = useRef<HTMLVideoElement>(null);
  const [playable, setPlayable] = useState(false);

  useEffect(() => {
    let timer: number | null = null;

    hls.current = new Hls({
      enableWorker: true,
      liveDurationInfinity: true,
      manifestLoadPolicy: {
        default: {
          maxTimeToFirstByteMs: 0,
          maxLoadTimeMs: 20000,
          timeoutRetry: {
            maxNumRetry: 1000,
            retryDelayMs: 1000,
            maxRetryDelayMs: 0,
          },
          errorRetry: {
            maxNumRetry: 1000,
            retryDelayMs: 1000,
            maxRetryDelayMs: 1000,
          },
        },
      },
    });

    if (hls.current && video.current) {
      hls.current.loadSource(props.src);
      hls.current.attachMedia(video.current);

      hls.current.on(Hls.Events.MANIFEST_PARSED, () => {
        console.debug('manifest parsed');
        if (hls.current) {
          hls.current.media?.play();
          setPlayable(true);
          if (timer) {
            clearTimeout(timer);
          }
        }
      });

      hls.current.on(Hls.Events.ERROR, (_, data) => {
        setPlayable(false);
        hls.current?.media?.pause();

        const loadSource = () => {
          if (!timer) {
            hls.current?.loadSource(props.src);
            timer = window.setTimeout(loadSource, 5000);
          }
        };

        if (!timer) {
          loadSource();
        }
      });
    }

    return () => {
      if (timer) {
        clearTimeout(timer);
      }

      if (hls.current) {
        hls.current.detachMedia();
        hls.current.destroy();
        hls.current = null;
      }
    };
  }, [props.src]);

  return (
    <MediaController style={props.style}>
      <MediaLoadingIndicator slot='centered-chrome' />
      <video ref={video} slot='media' preload='auto' muted crossOrigin='*' />
      {!playable ? (
        <MediaTextDisplay slot='centered-chrome'>
          {props.broadcastActive ? 'Opið fyrir streymi' : 'Útsending ekki hafin'}
        </MediaTextDisplay>
      ) : (
        <MediaControlBar>
          <MediaPlayButton />
          <MediaLiveButton />
          <MediaMuteButton />
          <MediaVolumeRange />
          <MediaFullscreenButton />
        </MediaControlBar>
      )}
    </MediaController>
  );
}
